export function asyncReadBlobAsText(file: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result);
            } else {
                reject(new Error('FileReader result is not a string'));
            }
        };
        reader.onerror = () => {
            reject(reader.error);
        };
        reader.readAsText(file);
    });
}

export interface PromptForTextFileOptions {
    /** Passed through to the file input's `accept` attribute, e.g. '.json'. */
    accept?: string;
}

/**
 * Opens the browser's file picker and resolves with the chosen file's
 * contents as text, or `undefined` if the picker is canceled or no single
 * file is chosen. Parsing the text is left to the caller.
 */
export function promptForTextFile(
    options: PromptForTextFileOptions = {}
): Promise<string | undefined> {
    return new Promise((resolve, reject) => {
        const pom = document.createElement('input');

        const onChange = (_changeEvent: Event) => {
            if (cleanup()) {
                const files = pom.files;
                if (files?.length !== 1) {
                    resolve(undefined);
                    return;
                }
                asyncReadBlobAsText(files[0]).then(resolve, reject);
            }
        };

        const onCancel = () => {
            if (cleanup()) resolve(undefined);
        };

        let settled = 0;
        const cleanup = () => {
            if (settled++) return false;
            pom.removeEventListener('change', onChange);
            pom.removeEventListener('cancel', onCancel);
            return true;
        };

        pom.setAttribute('type', 'file');
        if (options.accept) pom.setAttribute('accept', options.accept);
        pom.addEventListener('change', onChange);
        pom.addEventListener('cancel', onCancel);

        pom.click();
    });
}

export type PromptForJsonFileResult =
    | { accepted: true; value: unknown }
    | { accepted: false };

/**
 * Like `promptForTextFile`, but parses the chosen file as JSON. `accepted` is
 * false if the picker is cancelled or no single file is chosen. A file that
 * isn't valid JSON rejects with `JSON.parse`'s own SyntaxError.
 */
export async function promptForJsonFile(
    options?: PromptForTextFileOptions
): Promise<PromptForJsonFileResult> {
    const { accept = '.json', ...rest } = options || {};
    const text = await promptForTextFile({ accept, ...rest });
    if (text === undefined) return { accepted: false };
    return { accepted: true, value: JSON.parse(text) };
}
